import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { FileText, Lock, Shield } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface PolicySection {
  section_number: number;
  title: string;
  description: string;
}

export default function Tier1Policy() {
  const [sections, setSections] = useState<PolicySection[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadSections();
  }, []);

  const loadSections = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('tier1_policy_sections')
        .select('section_number, title, description')
        .order('section_number', { ascending: true });

      if (error) throw error;
      
      setSections(data || []);
    } catch (error) {
      console.error('Error loading sections:', error);
      toast({
        title: "Error",
        description: "Failed to load policy sections",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 flex items-center justify-center">
        <p className="text-muted-foreground">Loading policy sections...</p>
      </div>
    );
  }

  const staticSections = [
    {
      number: 1,
      title: "Policy Hierarchy & Document Status",
      description: "Purpose, document architecture, ownership and governance lifecycle"
    },
    {
      number: 2,
      title: "Fiduciary Framework & Regulatory Context",
      description: "Core fiduciary duties, UK regulatory framework, and compliance oversight"
    },
    {
      number: 3,
      title: "Investment Philosophy",
      description: "Asset allocation, cost discipline, responsible investment, and behavioural discipline"
    },
    {
      number: 4,
      title: "Statement of Investment Objectives",
      description: "Primary objectives, target market definition, and secondary objectives"
    },
    {
      number: 5,
      title: "The Benchmark-Driven Asset Allocation Policy",
      description: "AWM Composite Benchmark construction, strategic asset allocation, and review cycle"
    },
    {
      number: 6,
      title: "Permitted Asset Classes & Ranges",
      description: "Fixed-income, equities, alternatives, and portfolio constraints"
    },
    {
      number: 7,
      title: "Derivatives, Leverage & Collateral",
      description: "Philosophical stance, permitted instruments, and risk budget integration"
    },
    {
      number: 8,
      title: "Tactical Asset Allocation Guardrails",
      description: "Signal engine, position-sizing, turnover discipline, and governance oversight"
    },
    {
      number: 9,
      title: "Responsible Investment Vision",
      description: "ESG framework baseline, strategic aim, and 2025 transition plan"
    },
    {
      number: 10,
      title: "Liquidity & Cash Management Policy",
      description: "Data ingestion, operational response protocol, and stress testing"
    },
    {
      number: 11,
      title: "Risk Appetite & Quantitative Limits",
      description: "Core risk lenses, risk ceilings by profile, and escalation paths"
    },
    {
      number: 12,
      title: "Performance Measurement & Attribution",
      description: "Data acquisition, benchmark methodology, and attribution analysis"
    },
    {
      number: 13,
      title: "Cost Governance & Value-for-Money",
      description: "Cost components, automated calculations, and provider engagement"
    },
    {
      number: 14,
      title: "Governance Structure & Decision Rights",
      description: "Board structure, Investment Committee, Risk Oversight Panel"
    },
    {
      number: 15,
      title: "Conflict of Interest Standards",
      description: "Scope, conflict register, personal account dealing, and monitoring"
    },
    {
      number: 16,
      title: "Breach Escalation Protocol",
      description: "Detection, containment, remediation, and reporting procedures"
    },
    {
      number: 17,
      title: "Reporting, Transparency & Record-Keeping",
      description: "Client disclosure, management information, and retention schedules"
    },
    {
      number: 18,
      title: "Review Cycle & Change-Control",
      description: "Annual review, materiality thresholds, and amendment processes"
    },
    {
      number: 19,
      title: "Definitions & Abbreviations",
      description: "Comprehensive glossary of terms and acronyms used throughout"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="h-8 w-8 text-primary" />
            <h1 className="text-4xl font-bold text-foreground">Tier 1 Group Investment Policy</h1>
          </div>
          <div className="flex items-center gap-2 mb-4">
            <Badge variant="destructive" className="flex items-center gap-1">
              <Lock className="h-3 w-3" />
              INTERNAL CONFIDENTIAL
            </Badge>
            <Badge variant="outline">Version 1 - October 2025</Badge>
          </div>
          <p className="text-lg text-muted-foreground max-w-3xl">
            The Group Investment Policy fulfils two critical functions: codifying non-negotiable principles that define our fiduciary posture, 
            and constructing a legal and procedural bridge between high-level Board directives and day-to-day operations.
          </p>
        </div>

        <Separator className="my-8" />

        {/* Important Legal Information */}
        <Card className="mb-8 border-destructive/50 bg-destructive/5">
          <CardHeader>
            <CardTitle className="text-destructive">Important Legal Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p>
              This document is issued by <strong>Ascot Wealth Management Limited</strong> (Trading as Cape Berkshire Asset Management), 
              a company registered in England (No. 07428369) with its registered office at Scotch Corner, London Road, Sunningdale, 
              Berkshire, SL5 0ER. Authorised and regulated by the Financial Conduct Authority (FCA) under Financial Services Register Number 551744.
            </p>
            <p>
              This document is strictly for informational purposes only and does not constitute investment advice. All investments involve a 
              degree of risk, including the possible loss of principal. Past performance is not a reliable indicator of future results.
            </p>
            <p className="font-semibold">
              The contents of this document are <strong>confidential</strong> and intended solely for internal use. This is an internal 
              confidential class document and is not to be shared externally without senior management approval and a Non-Disclosure Agreement signed.
            </p>
            <p className="text-xs text-muted-foreground">
              © 2025 Ascot Wealth Management Limited. All rights reserved.
            </p>
          </CardContent>
        </Card>

        {/* Signatory Sheet */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Signatory Sheet</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="font-semibold text-muted-foreground mb-1">Author</p>
                <p className="text-foreground">Sam Hallett</p>
              </div>
              <div>
                <p className="font-semibold text-muted-foreground mb-1">Checker</p>
                <p className="text-foreground">Sam Hallett</p>
              </div>
              <div>
                <p className="font-semibold text-muted-foreground mb-1">Senior Sign Off</p>
                <p className="text-foreground">Mark Insley</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Policy Sections */}
        <div>
          <h2 className="text-2xl font-bold mb-6 text-foreground">Policy Sections</h2>
          {sections.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <p className="text-muted-foreground text-center">
                  No policy sections available. Please contact an administrator.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {sections.map((section) => (
                <Link
                  key={section.section_number}
                  to={`/partner/policy-centre/tier-1/section/${section.section_number}`}
                className="block group"
              >
                <Card className="h-full transition-all hover:shadow-lg hover:border-primary/50 group-hover:scale-[1.02]">
                  <CardHeader>
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <FileText className="h-5 w-5 text-primary" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <Badge variant="outline" className="mb-2">Section {section.section_number}</Badge>
                        <CardTitle className="text-base group-hover:text-primary transition-colors">
                          {section.title}
                        </CardTitle>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm">
                      {section.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </Link>
              ))}
            </div>
          )}
        </div>

        {/* Footer Note */}
        <Card className="mt-8 bg-muted/30">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground text-center">
              This policy forms part of the contractual architecture binding CBAM to its fiduciary obligations. 
              Non-compliance with this Group Investment Policy is both a regulatory breach and a potential breach of fiduciary duty.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}